import React from "react";
import { motion } from "framer-motion";
import { Clock, MapPin, Heart, Wallet, Star, Shield } from "lucide-react";

const PEOPLE_EATING_URL = "https://images.unsplash.com/photo-1657299156725-dc862fea3e11?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBlYXRpbmclMjBtb2Rlcm4lMjB2aWJyYW50JTIwZGlubmVyfGVufDB8fHx8MTc3NDk3ODYyOHww&ixlib=rb-4.1.0&q=85";
const BURGER_URL = "https://images.unsplash.com/photo-1633424234673-c8cd0f4df77b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxkZWxpY2lvdXMlMjBnb3VybWV0JTIwYnVyZ2VyJTIwZmx5aW5nJTIwaW5ncmVkaWVudHN8ZW58MHx8fHwxNzc0OTc4NjI2fDA&ixlib=rb-4.1.0&q=85";

const benefits = [
  {
    icon: Clock,
    title: "Entrega Rápida",
    description: "Recibe tu pedido en menos de 30 minutos. Seguimiento en tiempo real.",
  },
  {
    icon: MapPin,
    title: "Variedad Local",
    description: "Descubre los mejores restaurantes de tu zona. Comida auténtica cerca de ti.",
  },
  {
    icon: Heart,
    title: "Favoritos Guardados",
    description: "Guarda tus platos favoritos y repite pedidos con un solo toque.",
  },
  {
    icon: Wallet,
    title: "Ofertas Exclusivas",
    description: "Accede a descuentos y promociones solo disponibles en Apedir.",
  },
  {
    icon: Star,
    title: "Reseñas Reales",
    description: "Lee opiniones verificadas y descubre nuevos sabores.",
  },
  {
    icon: Shield,
    title: "Pago Seguro",
    description: "Múltiples métodos de pago con la máxima seguridad.",
  },
];

const CustomerBenefits = () => {
  return (
    <section id="clientes" className="py-24 bg-background" data-testid="customer-benefits-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="uppercase text-xs tracking-[0.2em] font-bold text-primary">
            Para Ti
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-foreground mt-4">
            Todo Lo Que Amas, En Un Solo Lugar
          </h2>
          <p className="text-base md:text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
            Apedir centraliza todos tus antojos. Pizza, sushi, tacos, postres... 
            lo que quieras, cuando lo quieras.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Image Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative">
              {/* Main Image */}
              <img
                src={PEOPLE_EATING_URL}
                alt="Personas disfrutando comida"
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover image-hover"
                data-testid="customer-benefits-image"
              />
              
              {/* Floating Burger Card */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -bottom-8 -right-8 w-48 neumorphic rounded-2xl p-4 shadow-xl"
                animate={{
                  y: [0, -10, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <img
                  src={BURGER_URL}
                  alt="Burger"
                  className="w-full h-32 object-cover rounded-xl"
                />
                <p className="text-sm font-bold mt-2 text-foreground">¡Antojo del día!</p>
                <p className="text-xs text-primary font-semibold">-20% OFF</p>
              </motion.div>

              {/* Background Decoration */}
              <div className="absolute -z-10 top-10 -left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
            </div>
          </motion.div>

          {/* Benefits Grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="group p-6 rounded-2xl border border-border hover:border-primary/30 transition-all duration-300 hover:shadow-soft bg-white"
                  data-testid={`benefit-card-${index + 1}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <benefit.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground mb-1">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CustomerBenefits;
